import type { IntlShape } from 'react-intl';

import type { PatientProfile } from '../../../contracts';
import type {
  SuggestedMatchCandidates,
  SuggestedPatientMatch,
} from '../suggested-matches';
import { toGenderLabel } from './to-gender-label';

export function toSuggestedPatientMatches(
  candidates: SuggestedMatchCandidates | null | undefined,
  patientProfiles: readonly PatientProfile[] | null | undefined,
  intl: IntlShape
): SuggestedPatientMatch[] {
  if (
    !candidates?.ranked_patient_profile_or_appointment.length ||
    !patientProfiles?.length
  ) {
    return [];
  }

  const profilesById = new Map(
    patientProfiles.flatMap((profile) => (profile.id ? [[profile.id, profile]] : []))
  );
  return candidates.ranked_patient_profile_or_appointment.flatMap((candidate) => {
    // Appointment candidates only include IDs today; render them once appointment
    // details are available to resolve into patient display data.
    if (candidate.entity_type !== 'patient_profile') return [];

    const patientProfile = profilesById.get(candidate.patient_profile_id);
    const fullName = patientProfile ? getPatientProfileDisplayName(patientProfile) : '';

    if (!patientProfile?.id || !fullName) return [];

    return [
      {
        id: `suggested-${patientProfile.id}`,
        patientProfileId: patientProfile.id,
        patientProfile,
        fullName,
        genderLabel: toGenderLabel(patientProfile.gender, intl),
        birthDate: patientProfile.birth_date ?? undefined,
        nextAppointmentDate: null,
        details: patientProfile.past_medical_history?.trim() || undefined,
      },
    ];
  });
}

function getPatientProfileDisplayName(patientProfile: PatientProfile): string {
  return (
    [patientProfile.first_name, patientProfile.last_name].filter(Boolean).join(' ') ||
    patientProfile.demographic_string ||
    ''
  ).trim();
}
